// sim/advisor.ts — EVOLUTION ADVISOR: a PURE, OBSERVE-ONLY read-model that tells the
// player how well a looted gene FITS their colony — Recommended / Strong Synergy /
// Neutral / Poor Fit, each with a short human reason.
//
// THE PROBLEM it solves: the player holds genes but cannot tell which one their colony
// actually NEEDS right now. genePower.ts already answers "how strong is this gene?"
// (raw power); diagnostics.ts already answers "what is holding my colony back?" (the
// binding BOTTLENECK). This layer is the JOIN of the two from the player's angle: given
// the colony's binding need (from diagnostics) and its emergent identity (from the
// personality layer), does THIS gene's EFFECT_CHANNEL set address that need or play to
// that identity? It is about FIT, not raw power — complementary to the Power Score.
//
// THE GOAL: "players should feel guided without losing agency." So this only ever
// surfaces a verdict + reason; it NEVER blocks an apply, auto-decides, or ranks for the
// player. Poor Fit is deliberately conservative — it fires only when the colony has a
// real unmet need this gene ignores, so it reads as guidance, not punishment.
//
// CRITICAL — this is NOT a tick System, and NOT sim state (mirrors src/sim/diagnostics.ts
// and src/sim/genePower.ts, the render-only layers in DESIGN.md §19/§24):
//   • It NEVER mutates state, draws RNG, or runs in the GameLoop.
//   • It is NOT folded into any state hash and is NOT part of the save payload. Compute
//     it before or after a tick and the world hash is byte-identical (replay-safe).
//   • It is a pure FUNCTION over already-deterministic integer inputs (the gene's
//     channels + the colony's bottleneck/identity), so same seed + same tick ⇒ identical
//     GeneAdvice.
//   • Returning a fresh object per call is fine: it is a read-model view called on
//     demand (web gene-inventory render, tests), not the per-tick hot path.

import { EFFECT_CHANNEL } from '../types.ts';
import { BOTTLENECK, BOTTLENECK_NAME } from './diagnostics.ts';
import { TRAIT, TRAIT_FORMING } from '../types.ts';
import { GENE_TYPE_BASE_CHANNEL } from './geneData.ts';
import { TRAIT_NAME } from './personality.ts';

// ---------------------------------------------------------------------------
// The verdict (ordered worst → best; `as const` map, NO enums — Node 22 strips types).
// ---------------------------------------------------------------------------

/**
 * How well a gene fits the colony, worst → best. The numeric order is meaningful
 * (a UI may sort or colour-ramp on it): PoorFit < Neutral < StrongSynergy < Recommended.
 */
export const ADVICE = {
  /** The colony has a real unmet need this gene does nothing for. */
  PoorFit: 0,
  /** A fine all-round upgrade with no special pull either way. */
  Neutral: 1,
  /** Plays to the colony's emergent identity (no pressing need to fix). */
  StrongSynergy: 2,
  /** Directly addresses the colony's single binding need right now. */
  Recommended: 3,
} as const;
export type Advice = (typeof ADVICE)[keyof typeof ADVICE];

/** Human name per ADVICE.* (index-aligned worst → best). */
export const ADVICE_NAME = [
  'Poor Fit', 'Neutral', 'Strong Synergy', 'Recommended',
] as const;

// ---------------------------------------------------------------------------
// Channel → COLONY NEED: which EFFECT_CHANNELs can ADDRESS each bottleneck.
//
// One row per BOTTLENECK.*, each a set of the channels that materially help with that
// limiter. A gene whose channel set intersects the row for the colony's current
// bottleneck is the one to apply. None / Storage have no gene LEVER (you fix Storage
// with structural rooms, not a body part), so their rows are empty — by design, those
// never produce a need-match, which keeps Poor Fit honest.
//   Defense   ← Combat, Defense, Toxicity, Regen   (win/survive the fight)
//   Food      ← Movement, Sensing, Size            (gather faster/further/more per trip)
//   Worker    ← Movement, Sensing, Size            (same economy levers as Food)
//   Expansion ← Movement, Sensing                  (reach + scout new ground)
//   Larvae    ← Regen, Size, Defense               (keep the population alive/persisting)
//   Storage   ← (none — needs a Storage chamber, not a gene)
// ---------------------------------------------------------------------------

const C = EFFECT_CHANNEL;

/** Indexed by BOTTLENECK.*; each entry lists the channels that address that need. */
const NEED_CHANNELS: readonly (readonly number[])[] = [
  /* None      */ [],
  /* Food      */ [C.Movement, C.Sensing, C.Size],
  /* Storage   */ [],
  /* Worker    */ [C.Movement, C.Sensing, C.Size],
  /* Larvae    */ [C.Regen, C.Size, C.Defense],
  /* Defense   */ [C.Combat, C.Defense, C.Toxicity, C.Regen],
  /* Expansion */ [C.Movement, C.Sensing],
];

/**
 * The human reason for a Recommended verdict, indexed by BOTTLENECK.*. Each references
 * the NEED the gene is meeting (not the channel) so the player reads WHY. None / Storage
 * never reach this table (no gene lever), but carry a safe generic line for completeness.
 */
const NEED_REASON: readonly string[] = [
  /* None      */ 'A solid all-round upgrade.',
  /* Food      */ 'Improves food production — your colony is food-limited.',
  /* Storage   */ 'A solid all-round upgrade.',
  /* Worker    */ 'Boosts your worker economy — your colony is short on labour.',
  /* Larvae    */ 'Bolsters survival — keeps your population from stalling.',
  /* Defense   */ 'Strengthens your soldiers — your nest is under predator pressure.',
  /* Expansion */ 'Helps you expand — your colony is capped.',
];

// ---------------------------------------------------------------------------
// Channel → COLONY IDENTITY: which channels each emergent personality FAVOURS.
//
// One row per TRAIT.* (the personality enum: Aggressive, Expansionist, Industrial,
// Defensive, Adaptive, Predatory). A gene whose channel set intersects the colony's
// identity row "plays to character". Adaptive is the flexible identity — it favours
// EVERY channel (broad synergy), so an Adaptive colony reads almost any gene as
// synergistic. This is the SECOND-priority match (below a binding need).
//   Aggressive  ← Combat, Toxicity, Defense
//   Expansionist← Movement, Sensing
//   Industrial  ← Movement, Sensing, Size   (worker economy)
//   Defensive   ← Defense, Regen
//   Adaptive    ← any                        (flexible — broad synergy)
//   Predatory   ← Combat, Toxicity, Defense  (same offensive lean as Aggressive)
// ---------------------------------------------------------------------------

/** All seven channels — the Adaptive identity favours every one of them. */
const ALL_CHANNELS: readonly number[] = [
  C.Movement, C.Combat, C.Sensing, C.Defense, C.Toxicity, C.Regen, C.Size,
];

/** Indexed by TRAIT.*; each entry lists the channels that identity favours. */
const IDENTITY_CHANNELS: readonly (readonly number[])[] = [
  /* Aggressive   */ [C.Combat, C.Toxicity, C.Defense],
  /* Expansionist */ [C.Movement, C.Sensing],
  /* Industrial   */ [C.Movement, C.Sensing, C.Size],
  /* Defensive    */ [C.Defense, C.Regen],
  /* Adaptive     */ ALL_CHANNELS,
  /* Predatory    */ [C.Combat, C.Toxicity, C.Defense],
];

// ---------------------------------------------------------------------------
// Inputs / output.
// ---------------------------------------------------------------------------

/**
 * The gene being judged. `channels` is its set of active EFFECT_CHANNELs (base-type
 * channel plus any trait channels the caller resolved). If omitted, the advisor derives
 * it from the gene's base type alone (`GENE_TYPE_BASE_CHANNEL[type]`). `rarity` is
 * accepted for parity with the Power Score / future tuning; the FIT verdict deliberately
 * does not weight rarity (a rare gene that ignores your need is still a poor fit).
 */
export interface GeneAdviceInput {
  readonly type: number;
  readonly rarity: number;
  readonly channels?: readonly number[] | undefined;
}

/**
 * The colony's decision context. `bottleneck` is BOTTLENECK.* from
 * computeColonyDiagnostics (the colony's single binding need). `identity` is the
 * emerged personality TRAIT.* (-1 / TRAIT_FORMING when none has crystallized yet).
 */
export interface AdviceContext {
  readonly bottleneck: number;
  readonly identity: number;
  /**
   * OPTIONAL co-limiters (the colony's SECONDARY binding needs, primary excluded) — i.e.
   * `computeColonyDiagnostics().secondaryBottlenecks`. When supplied, a gene that doesn't
   * address the PRIMARY need but DOES address a secondary one reads as Strong Synergy (it
   * genuinely helps a real, if lesser, limiter) rather than slipping to Poor Fit. Absent /
   * empty ⇒ the verdict is identical to the single-need model (byte-identical).
   */
  readonly secondaryBottlenecks?: readonly number[] | undefined;
}

/** The advisor's verdict for one gene against one colony context. */
export interface GeneAdvice {
  /** The verdict (ADVICE.*). */
  readonly verdict: Advice;
  /** Human name of `verdict` (ADVICE_NAME). */
  readonly verdictName: string;
  /** A short, player-facing reason for the verdict. */
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// Helpers (pure; allocation-light).
// ---------------------------------------------------------------------------

/** True if any channel in `channels` appears in `row`. Small fixed sets, so the nested
 *  scan is cheap (≤7 × ≤7) and allocates nothing — no Set, no closure. */
function intersects(channels: readonly number[], row: readonly number[]): boolean {
  for (let i = 0; i < channels.length; i++) {
    const ch = channels[i] as number;
    for (let j = 0; j < row.length; j++) {
      if ((row[j] as number) === ch) return true;
    }
  }
  return false;
}

/** Resolve a gene's channel set: the caller-supplied set, or the base-type channel. */
function resolveChannels(gene: GeneAdviceInput): readonly number[] {
  if (gene.channels && gene.channels.length > 0) return gene.channels;
  const base = GENE_TYPE_BASE_CHANNEL[gene.type];
  return base === undefined ? [] : [base as number];
}

/** Lookup a need's channel row safely (out-of-range bottleneck ⇒ empty, i.e. None). */
function needRow(bottleneck: number): readonly number[] {
  return (NEED_CHANNELS[bottleneck] as readonly number[] | undefined) ?? [];
}

/** Lookup an identity's channel row safely (forming / out-of-range ⇒ empty). */
function identityRow(identity: number): readonly number[] {
  if (identity === TRAIT_FORMING || identity < 0) return [];
  return (IDENTITY_CHANNELS[identity] as readonly number[] | undefined) ?? [];
}

// ---------------------------------------------------------------------------
// The verdict cascade (first match wins). Players read THIS.
// ---------------------------------------------------------------------------

/**
 * Advise on one gene against one colony context. PURE — reads only its integer inputs;
 * no mutation, no RNG, no clock. Allocates one returned object (a read-model view, not
 * the hot path).
 *
 * CASCADE (deterministic, first match wins):
 *   1. The colony has a binding need (bottleneck ≠ None) AND a gene channel ADDRESSES it
 *      → Recommended (reason references the NEED).
 *   2. A gene channel addresses a SECONDARY limiter (a co-limiter, primary excluded) when
 *      the caller supplies them → Strong Synergy (reason names that lesser need). Skipped
 *      when no secondary needs are passed, so the single-need model is byte-identical.
 *   3. A gene channel is FAVOURED by the colony's emergent identity
 *      → Strong Synergy (reason names the identity).
 *   4. No need addressed AND no identity synergy AND the colony HAS a pressing
 *      bottleneck (≠ None) → Poor Fit (gentle — it ignores a real, current need).
 *   5. Otherwise → Neutral (a solid all-round upgrade; nothing urgent to fix and no
 *      special identity pull).
 */
export function adviseGene(gene: GeneAdviceInput, ctx: AdviceContext): GeneAdvice {
  const channels = resolveChannels(gene);
  const hasNeed = ctx.bottleneck !== BOTTLENECK.None;

  // 1. Binding (PRIMARY) need this gene addresses → Recommended.
  if (hasNeed && intersects(channels, needRow(ctx.bottleneck))) {
    return advice(ADVICE.Recommended, NEED_REASON[ctx.bottleneck] ?? NEED_REASON[BOTTLENECK.None]!);
  }

  // 2. Addresses a SECONDARY limiter (a co-limiter, not the top one) → Strong Synergy.
  //    Only when the caller passes the colony's secondary needs; absent ⇒ skipped, so the
  //    verdict matches the original single-need cascade exactly.
  const secondary = ctx.secondaryBottlenecks;
  if (secondary !== undefined) {
    for (let i = 0; i < secondary.length; i++) {
      const need = secondary[i] as number;
      if (need !== BOTTLENECK.None && intersects(channels, needRow(need))) {
        const name = BOTTLENECK_NAME[need] ?? 'colony';
        return advice(ADVICE.StrongSynergy, `Also eases your ${name} limiter — a secondary need.`);
      }
    }
  }

  // 3. Plays to the colony's emergent identity → Strong Synergy.
  if (intersects(channels, identityRow(ctx.identity))) {
    const name = TRAIT_NAME[ctx.identity] ?? 'colony';
    return advice(ADVICE.StrongSynergy, `Synergizes with your ${name} colony.`);
  }

  // 4. A real unmet need this gene ignores → Poor Fit (conservative: only when a
  //    pressing bottleneck exists, so a healthy colony never sees a "poor" verdict).
  if (hasNeed) {
    return advice(ADVICE.PoorFit, 'Limited help for what your colony needs right now.');
  }

  // 5. Nothing pressing, no identity pull → a clean all-round pick.
  return advice(ADVICE.Neutral, 'A solid all-round upgrade.');
}

/** Build a GeneAdvice (centralises the name lookup so verdict + name never drift). */
function advice(verdict: Advice, reason: string): GeneAdvice {
  return { verdict, verdictName: ADVICE_NAME[verdict], reason };
}
